import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NotesDialogComponent } from '../notes-dialog/notes-dialog.component';
import { SharedReportDialogComponent } from '../report-dialog/report-dialog.component';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { SuggestionEngineService } from '../../suggestion-engine/suggestion-engine.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { LifeEventComponent } from '../life-event-dialog/life-event-dialog.component';
import { saveAs } from '@progress/kendo-file-saver';
import { Platform } from '@angular/cdk/platform';
import { EnvironmentService } from '../../services/environment.service';
import { FormControl } from '@angular/forms';
import { PostTradeService } from '../../post-trade/post-trade.service';
import { MarkdownService } from 'ngx-markdown';
import {
  AiSummaryResponse,
  AiSummarySearchResponse,
  SearchAiSummary,
} from '../../models/post-trade-models';
import { Notes } from '../../models/general-models';
import { NgxLoadingModule } from 'ngx-loading';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AiSummaryComponent } from '../../ai-summary/ai-summary.component';
import { CommonModule, DatePipe, KeyValuePipe } from '@angular/common';

interface Prompt {
  value: string;
  display: string;
}

@Component({
  selector: 'app-client-info',
  templateUrl: './client-info.component.html',
  styleUrls: ['./client-info.component.scss'],
  imports: [
    NgxLoadingModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatFormFieldModule,
    MatSelectModule,
    AiSummaryComponent,
    KeyValuePipe,
    DatePipe,
    CommonModule,
    MatTooltipModule,
  ],
})
export class ClientInfoComponent implements OnInit {
  private rbs = inject(RightBridgeApiService);
  private ses = inject(SuggestionEngineService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private ss = inject(SessionStorageService);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);
  private ptSvc = inject(PostTradeService);
  private markdownService = inject(MarkdownService);
  private changeDetector = inject(ChangeDetectorRef);

  clientData;
  @Output() profileNavigate = new EventEmitter();
  @Input() client: string;
  @Input() name: string;
  loadingAiSummary: boolean;
  aiError: string;
  @Input() set dataSet(data) {
    this.clientData = data;
    if (data && Object.keys(data).length) {
      this.dataUpdate();
    }
  }
  aiSummaryText: string;
  prompts: Prompt[] = [];
  DEFAULT_PROMPTS = [
    { value: 'SEAISummaryGeneral', display: 'General' },
    { value: 'SEAISummaryClient', display: 'Client Facing' },
  ];
  promptControl = new FormControl(null);

  private environment;
  private baseUrl: string;
  private rights: string[];

  info;
  accounts = { investment: [], annuity: [], lifeInsurance: [], longTerm: [] };
  notes: Notes;
  profList;
  noAccounts = true;
  tabIndex = 0;
  tabsOpen = true;
  isProspect = false;
  addToList = true;
  loading = false;

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;

    this.profList = this.ss.get('seProfileList');
    this.rights = this.ss.get('rights');

    if (this.rights.includes('AISummarySE')) {
      this.prompts =
        this.ss.get('globals')?.GlobalsGroup?.SEAISummaryPrompts ||
        this.DEFAULT_PROMPTS;
      this.searchAISummary();
    }
  }

  searchAISummary() {
    const aiSearchPayload: SearchAiSummary = {
      search: {
        CreatedDate: {
          start: new Date(0).toISOString(),
          end: new Date().toISOString(),
        },
        ReferenceID: { searchString: this.client },
      },
      skip: '0',
      take: '1',
      sort: { CreatedDate: 'desc' },
    };

    this.ptSvc.getAiSummarySearch(aiSearchPayload).subscribe({
      next: (res: AiSummarySearchResponse) => {
        (async () => {
          this.aiSummaryText = await this.markdownService.parse(
            res?.data[0]?.Text || ''
          );
        })();
        if (!this.aiSummaryText) {
          this.aiError =
            'No existing AI summary found. Please generate a summary.';
        }
      },
      error: () => {
        this.aiSummaryText = null;
        this.aiError =
          'An unexpected error occured while attempting to retrieve previously generated summary. Please try again later or regenerate a new summary.';
      },
    });
  }

  dataUpdate() {
    this.accounts = {
      investment: this.clientData.InvestmentAccount,
      annuity: this.clientData.AnnuityContract,
      lifeInsurance: this.clientData.LIPolicy,
      longTerm: this.clientData.OtherPolicy,
    };

    this.noAccounts =
      !this.accounts.longTerm['data'].length &&
      !this.accounts.lifeInsurance['data'].length &&
      !this.accounts.annuity['data'].length &&
      !this.accounts.investment['data'].length;

    Object.keys(this.clientData).forEach(el => {
      if (
        !el.includes('InvestmentAccount') ||
        !el.includes('AnnuityContract') ||
        !el.includes('LIPolicy') ||
        !el.includes('OtherPolicy') ||
        el !== 'needs'
      ) {
        this.info = !this.info ? {} : this.info;
        if (this.clientData[el] && this.clientData[el].display) {
          this.info[el.replace('.', '_')] = {
            label: this.clientData[el].label,
            display: this.clientData[el].display[0],
            value: this.clientData[el].raw[0],
          };
        }
      }
    });
    this.addToList = !this.clientData.inCallList;
    this.isProspect = this['info.Client^IsProspect'];
    this.getNotes(this.client);
  }

  addToCallList() {
    const profile = [this.client];
    this.ses.addToCallList(profile).subscribe(() => {
      console.info('Client added to call list');
      this.profileNavigate.emit(this.client);
    });
  }

  removeFromCallList() {
    const profile = [this.client];
    this.ses.removeFromCallList(profile).subscribe(() => {
      console.info('Client removed from call list');
      this.profileNavigate.emit(this.client);
    });
  }

  dlReport() {
    const dialogRef = this.dialog.open(SharedReportDialogComponent, {
      panelClass: 'report-dialog',
      data: { app: 'se', bulk: false, needs: this.clientData.needs },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        this.loading = true;
        const iosSafari =
          this.pltfrm.IOS ||
          (navigator?.['userAgentData']?.platform === 'macOS' &&
            navigator.maxTouchPoints > 1);

        const client = [this.client];
        const options = { ...result.data };
        this.rbs.getClientReport(client, options).subscribe(x => {
          const filename = result.data.filename
            ? result.data.filename + '.pdf'
            : this.name + '-report.pdf';

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(x);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.loading = false;
          };
        });
      }
    });
  }

  editProfile() {
    this.router.navigate(['/se/client/edit/' + this.client]);
  }

  getNotes(clnt) {
    this.rbs.getNotes(clnt).subscribe(x => {
      this.notes = x;
    });
  }

  addNote() {
    const dialogRef = this.dialog.open(NotesDialogComponent, {
      panelClass: 'notes-dialog',
      data: { type: null, headline: null, placeholder: null },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        this.rbs.addNote(this.client, result.data).subscribe(() => {
          console.info('Note added');
          this.getNotes(this.client);
        });
      }
    });
  }

  togglePin(note) {
    if (note.isPinned) {
      this.rbs.pinToggle(note.profileId, note.note, false).subscribe(() => {
        const idx = this.notes.notes.findIndex(nt => nt.note === note.note);
        this.notes.notes[idx].isPinned = false;
      });
    } else {
      this.rbs.pinToggle(note.profileId, note.note, true).subscribe(() => {
        const idx = this.notes.notes.findIndex(nt => nt.note === note.note);
        this.notes.notes[idx].isPinned = true;
      });
    }
    setTimeout(() => {
      this.getNotes(this.client);
    });
  }

  profNav(client, dir) {
    const idx = this.profList.indexOf(client);
    let newIdx = dir === 'next' ? idx + 1 : idx - 1;
    newIdx = newIdx < 0 ? this.profList.length - 1 : newIdx;
    const newProf =
      dir === 'next' ? this.profList[newIdx] : this.profList[newIdx];
    this.profileNavigate.emit(newProf);
  }

  addLifeEvent() {
    const dialogRef = this.dialog.open(LifeEventComponent, {
      panelClass: 'notes-dialog',
      data: { type: null, headline: null, placeholder: null },
    });
    const user = this.ss.get('globals').user.id;
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        this.ses
          .saveLifeEvent(
            this.client,
            result.data.type,
            user,
            result.data.date.toLocaleDateString('en-US'),
            result.data.note
          )
          .subscribe(() => {
            console.info('Life event added');
            this.getNotes(this.client);
            this.profileNavigate.emit(this.client);
          });
      }
    });
  }

  tabClick(ev, idx) {
    this.tabsOpen = idx === this.tabIndex ? !this.tabsOpen : true;
  }

  generateSummary() {
    this.loadingAiSummary = true;
    const requestData = {
      type: 'aicasereview',
      profileids: [this.client],
      promptname: this.promptControl.value,
      group: 'SuggestionAISummary',
    };

    this.ptSvc.aiBulkProcess(requestData).subscribe({
      next: (resp: AiSummaryResponse) => {
        if (resp.result) {
          (async () => {
            this.aiSummaryText = await this.markdownService.parse(resp.result);
          })();
        } else {
          this.aiSummaryText = '';
          this.aiError =
            'No summary could be generated. Please try again or reach out to your administrator.';
        }
        this.loadingAiSummary = false;
      },
      error: err => {
        console.warn('Error: ', err);
        this.aiSummaryText = '';
        this.aiError =
          'No summary could be generated. Please try again or reach out to your administrator.';
        this.loadingAiSummary = false;
      },
    });
  }
}
